/**
 * WebSocket hook for system notifications (e.g. version updates).
 *
 * Keeps one long-lived connection to /api/v1/notifications/ws.
 * On connect the server sends `latest_notification`; later broadcasts
 * arrive as `new_notification`. Reconnects only after unexpected close,
 * with exponential backoff. Dismissed codes are persisted in localStorage.
 */

import { useEffect, useState, useRef, useCallback } from 'react';

const STORAGE_KEY = 'jarvis_dismissed_notifications';
const BASE_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

export interface SystemNotification {
  id: number;
  code: string;
  message: string;
}

interface WsMessage {
  type: string;
  data: SystemNotification;
  timestamp: number;
}

/** Read the set of dismissed codes from localStorage */
const getDismissedCodes = (): Set<string> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
};

/** Persist a newly-dismissed code */
const addDismissedCode = (code: string) => {
  const codes = getDismissedCodes();
  codes.add(code);
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...codes]));
};

const buildWsUrl = (): string => {
  const wsBaseUrl = import.meta.env.VITE_WS_BASE_URL as string | undefined;
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;

  let baseUrl: string;
  if (window.location.hostname === 'jar-vis.com' || window.location.hostname.endsWith('.jar-vis.com')) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    baseUrl = `${protocol}//${window.location.host}`;
  } else if (wsBaseUrl) {
    baseUrl = wsBaseUrl.endsWith('/') ? wsBaseUrl.slice(0, -1) : wsBaseUrl;
  } else if (apiBaseUrl) {
    baseUrl = apiBaseUrl.replace(/^http/, 'ws').replace(/^https/, 'wss');
    if (baseUrl.endsWith('/')) baseUrl = baseUrl.slice(0, -1);
  } else {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    baseUrl = `${protocol}//${window.location.host}`;
  }

  const accessToken = localStorage.getItem('accessToken');
  const url = `${baseUrl}/api/v1/notifications/ws`;
  return accessToken ? `${url}?token=${accessToken}` : url;
};

const isNotificationMessage = (type: string): boolean =>
  type === 'latest_notification' || type === 'new_notification';

export interface UseNotificationWebSocketReturn {
  notification: SystemNotification | null;
  dismiss: () => void;
}

export const useNotificationWebSocket = (): UseNotificationWebSocketReturn => {
  const [notification, setNotification] = useState<SystemNotification | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const intentionalCloseRef = useRef(false);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const applyNotification = useCallback((data: SystemNotification | null | undefined) => {
    if (!data?.code) return;
    const dismissed = getDismissedCodes();
    if (!dismissed.has(data.code)) {
      setNotification(data);
    }
  }, []);

  const closeSocket = useCallback(() => {
    if (!wsRef.current) return;
    const ws = wsRef.current;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close();
    }
    wsRef.current = null;
  }, []);

  const connect = useCallback(() => {
    if (intentionalCloseRef.current) return;

    // Avoid duplicate sockets while one is already open/connecting.
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    closeSocket();

    try {
      const ws = new WebSocket(buildWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message: WsMessage = JSON.parse(event.data);
          if (isNotificationMessage(message.type)) {
            applyNotification(message.data);
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onerror = () => {
        // onclose handles reconnect
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (intentionalCloseRef.current) return;

        const attempt = reconnectAttemptsRef.current;
        const delay = Math.min(
          BASE_RECONNECT_DELAY_MS * 2 ** attempt,
          MAX_RECONNECT_DELAY_MS,
        );
        reconnectAttemptsRef.current = attempt + 1;

        clearReconnectTimer();
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      };
    } catch {
      if (intentionalCloseRef.current) return;
      const attempt = reconnectAttemptsRef.current;
      const delay = Math.min(
        BASE_RECONNECT_DELAY_MS * 2 ** attempt,
        MAX_RECONNECT_DELAY_MS,
      );
      reconnectAttemptsRef.current = attempt + 1;
      clearReconnectTimer();
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
    }
  }, [applyNotification, clearReconnectTimer, closeSocket]);

  const dismiss = useCallback(() => {
    if (notification) {
      addDismissedCode(notification.code);
      setNotification(null);
    }
  }, [notification]);

  useEffect(() => {
    intentionalCloseRef.current = false;
    connect();

    return () => {
      intentionalCloseRef.current = true;
      clearReconnectTimer();
      closeSocket();
    };
  }, [connect, clearReconnectTimer, closeSocket]);

  return { notification, dismiss };
};

export default useNotificationWebSocket;
